import { Grid } from "@mui/material";
import { addDays, addMonths, isAfter, isBefore, subDays } from "date-fns";
import React, { useCallback, useMemo, useState } from "react";

import { Page } from "../../../../models/api/page";
import { CustomDate } from "../../../../models/custom-date/custom-date";
import { EmployeeRef } from "../../../../models/employees";
import { TnAAbsenceDTO, TnACounterDTO, TnAEmployeeActualConfigDTO } from "../../../../models/tna";
import { TnADataDTO } from "../../../../models/tna/tna-data/tna-data-dto";
import {
  TnATimesheetConfirmationFilter,
  TnATimesheetConfirmationsDTO,
} from "../../../../models/tna-timesheet-confirmation";
import { loadTnACounters, loadTnaData } from "../../../../services/tna-clockings/tna-data-api.service";
import { loadEETnAActualConfig } from "../../../../services/tna-config/tna-employee-config-api.service";
import { TnATimesheetConfirmationsApiService } from "../../../../services/tna-timesheet-confirmations/tna-timesheet-confirmations-api.service";
import { SIGN_OFFS_PAGE_SIZE } from "../../../../views/legal-entities/EmployeeDataPage/utils";
import AddEditAbsence from "../../../MyCalendar/AddEditAbsence/AddEditAbsence";

interface Props {
  employee: EmployeeRef | null;
  absence: TnAAbsenceDTO;
  handleNotifyParent: () => void;
  allowedToApproveReject: boolean;
  viewMode: boolean;
  children: React.ReactNode;
  year?: number;
  month?: number;
  forManager: boolean;
  minDate?: Date;
  maxDate?: Date;
  isEditable?: boolean;
  allowedOnlyManagersAction?: boolean;
  allowedOnlyApproveReject?: boolean | undefined;
  showAdditionalInfoMessage?: boolean;
  additionalInfoMessage?: string;
}

interface State {
  loading: boolean;
  error: string;
  tnAActualConfig: TnAEmployeeActualConfigDTO | null;
  tnAData: TnADataDTO | null;
  employeeTimeSheetSignOff: TnATimesheetConfirmationsDTO | null;
  counters: TnACounterDTO[];
}

const EditAbsenceWrapper = ({
  employee,
  absence,
  handleNotifyParent,
  allowedToApproveReject,
  viewMode,
  children,
  year,
  month,
  forManager,
  minDate,
  maxDate,
  isEditable,
  allowedOnlyManagersAction,
  allowedOnlyApproveReject,
  showAdditionalInfoMessage,
  additionalInfoMessage,
}: Props) => {
  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    tnAActualConfig: null,
    tnAData: null,
    employeeTimeSheetSignOff: null,
    counters: [],
  });

  const timesheetConfirmationsApiService = useMemo(() => new TnATimesheetConfirmationsApiService(), []);

  const getMinDate = useCallback(() => {
    const tsStartDate = CustomDate.ToDate(state.tnAData?.tsStartDate ?? null) ?? new Date(2099, 11, 31);
    const ctStartDate = CustomDate.ToDate(state.tnAData?.ctStartDate ?? null) ?? new Date(2099, 11, 31);
    const calendarStartDate = isAfter(ctStartDate, tsStartDate) ? ctStartDate : tsStartDate;

    const lastSignedOffDate =
      CustomDate.ToDate(state.employeeTimeSheetSignOff?.asOfDate ?? null) ??
      CustomDate.ToDate(state.employeeTimeSheetSignOff?.signedAsOfDate ?? null) ??
      new Date(0, 0, 0);
    const canSignOffSince = addDays(lastSignedOffDate, 1);

    const calendarMinDate = isAfter(canSignOffSince, calendarStartDate) ? canSignOffSince : calendarStartDate;

    return calendarMinDate;
  }, [state.tnAData?.tsStartDate, state.tnAData?.ctStartDate, state.employeeTimeSheetSignOff?.signedAsOfDate]);

  const getMaxDate = useCallback(() => {
    const tsEndDate = CustomDate.ToDate(state.tnAData?.tsEndDate ?? null) ?? new Date(2099, 11, 31);
    const ctEndDate = CustomDate.ToDate(state.tnAData?.ctEndDate ?? null) ?? new Date(2099, 11, 31);
    const calendarEndDate = isBefore(ctEndDate, tsEndDate) ? ctEndDate : tsEndDate;

    return calendarEndDate;
  }, [state.tnAData?.tsEndDate, state.tnAData?.ctEndDate]);

  const loadVacationCountersCallback = useCallback(
    async (employeeId: string, forManager: boolean, signal?: AbortSignal) => {
      setState((state) => ({
        ...state,
        counters: [],
        error: "",
      }));

      if (!forManager) return;

      try {
        const startYear = absence.fromDate!.year;
        const endYear = absence.toDate!.year;

        const { items, error } = await loadTnACounters(
          {
            employeeId,
            years: [startYear, endYear],
          },
          forManager,
          signal
        );

        if (error) {
          setState((state) => ({ ...state, error }));
        } else {
          setState((state) => ({ ...state, counters: items, error: "" }));
        }
      } catch (error) {
        setState((state) => ({ ...state, error: error as string }));
      }
    },
    [viewMode, absence]
  );

  const loadEmployeeTnADataCallback = useCallback(
    async (employeeId: string, year: number, month: number, forManager: boolean, signal?: AbortSignal) => {
      setState((state) => ({
        ...state,
        tnAData: null,
        error: "",
      }));

      if (!forManager) return;

      try {
        // for start and end date use the start and end of week to show prev & next month data
        let dayToStart = new Date(Date.UTC(year, month, 1));
        let dayToEnd = addDays(addMonths(dayToStart, 1), -1);

        dayToStart = addDays(subDays(dayToStart, dayToStart.getDay()), 1);
        dayToEnd = addDays(dayToEnd, 6 - dayToEnd.getDay() + 8);

        const start = dayToStart;
        const end = dayToEnd;

        const { items, error } = await loadTnaData(
          {
            employeeId,
            fromDate: {
              year: start.getFullYear(),
              month: start.getMonth() + 1,
              day: start.getDate(),
            },
            toDate: {
              year: end.getFullYear(),
              month: end.getMonth() + 1,
              day: end.getDate(),
            },
          },
          forManager,
          signal
        );

        if (error) {
          setState((state) => ({ ...state, error }));
        } else {
          const tnAData = items && items.length ? items[0] : null;

          setState((state) => ({
            ...state,
            tnAData, // error is handled by top callback
          }));

          await loadVacationCountersCallback(employeeId, forManager);
        }
      } catch (error) {
        setState((state) => ({ ...state, error: error as string }));
      }
    },
    [employee?.id, year, month, viewMode, loadVacationCountersCallback]
  );

  const loadEmployeeTimesheetSignOffCallback = useCallback(
    async (paging: Page, year?: number | null, signal?: AbortSignal) => {
      try {
        timesheetConfirmationsApiService.useLoadItemsPath("/GetTnAEmployeeTimesheetSignOffRequest");

        const response = await timesheetConfirmationsApiService.loadItems(
          {
            employeeId: employee!.id,
            year,
            paging,
          } as TnATimesheetConfirmationFilter,
          undefined,
          undefined,
          undefined,
          undefined,
          60,
          signal
        );

        if (!response.error) {
          setState((state) => ({
            ...state,
            employeeTimeSheetSignOff: response.items[0],
          }));
        }
      } catch (error) {
        setState((state) => ({ ...state, error: error as string }));
      }
    },
    [timesheetConfirmationsApiService, employee]
  );

  const loadEETnAActualConfigCallback = useCallback(
    async (employeeId: string, signal?: AbortSignal) => {
      try {
        setState((state) => ({
          ...state,
          tnAData: null,
          error: "",
        }));

        const atDate = {
          year,
          month: (month ?? 0) >= 0 ? (month ?? 0) + 1 : new Date().getMonth() + 1,
          day: 1,
        } as CustomDate;

        const { items, error } = await loadEETnAActualConfig(employeeId, atDate, signal);

        if (error) {
          setState((state) => ({ ...state, error, tnAData: null, tnAActualConfig: null }));
        } else {
          const tnAActualConfig = items && items.length ? items[0] : null;

          setState((state) => ({
            ...state,
            tnAActualConfig,
            tnAActualConfigLoaded: true,
            error: "",
          }));

          if (tnAActualConfig && tnAActualConfig?.tnAConfig && year && month) {
            await loadEmployeeTnADataCallback(employee!.id, year, month, forManager, signal);
          }
        }
      } catch (error) {
        setState((state) => ({ ...state, error: error as string }));
      }
    },
    [employee?.id, year, month, forManager, loadEmployeeTnADataCallback]
  );

  const loadData = useCallback(
    async (abortController?: AbortController) => {
      setState((state) => ({ ...state, loading: true, error: "", counters: [] }));

      if (minDate && maxDate) {
        await loadVacationCountersCallback(employee!.id, forManager, abortController?.signal);
      } else {
        await loadEETnAActualConfigCallback(employee!.id, abortController?.signal);

        await loadEmployeeTimesheetSignOffCallback(
          { pageNumber: 0, pageSize: SIGN_OFFS_PAGE_SIZE },
          null,
          abortController?.signal
        );
      }

      setState((state) => ({ ...state, loading: false }));
    },
    [loadEETnAActualConfigCallback]
  );

  return (
    <Grid
      sx={{
        "&:hover .MuiTypography-root": {
          cursor: "pointer",
        },
      }}
    >
      <Grid
        onClick={() => {
          employee?.id && forManager && loadData();
        }}
      >
        <AddEditAbsence
          effect={"edit"}
          employee={employee}
          handleNotifyParent={handleNotifyParent}
          item={absence}
          forManager={forManager}
          allowedToApproveReject={allowedToApproveReject}
          viewMode={viewMode || state.loading || !!state.error}
          minDate={minDate ?? getMinDate()}
          maxDate={maxDate ?? getMaxDate()}
          allowedOnlyManagersAction={allowedOnlyManagersAction}
          parentIsLoading={state.loading}
          counters={state.counters}
          isEditable={isEditable}
          allowedOnlyApproveReject={allowedOnlyApproveReject}
          showAdditionalInfoMessage={showAdditionalInfoMessage}
          additionalInfoMessage={additionalInfoMessage}
        >
          {children}
        </AddEditAbsence>
      </Grid>
    </Grid>
  );
};

export default EditAbsenceWrapper;
